import { boundFitsType } from '../utils/dateBounds';
import { ceilingFor, formatLimit, maxLengthOf } from '../utils/elementFormat/text';
import { decimalsOf, stepFits } from '../utils/elementFormat/number';

// UI policy: which element options the card-definition form offers for each
// data type. This drives show/hide behavior as the data type changes.
//
// To offer an option for another data type, add its key to that type's list.
// Option keys correspond to fields under element.options.

// 'drivenFrom' is offered for EVERY data type: any element can be the target of
// a driven field, and which SOURCE elements may feed it is a question the
// coercion matrix answers inside the driven-from modal — not something this
// policy can decide from the target type alone.
// `requiredWhen` rides with `required`: it is the same question asked
// conditionally, it applies to every data type ("reason for variance" is
// text, "mileage" a number, "signer" a lookup), and it must survive a type
// change exactly as `required` does.
const COMMON_OPTIONS = ['required', 'requiredWhen', 'drivenFrom'];

const OPTION_POLICY = {
	default: [...COMMON_OPTIONS],
	// 'enableAutocomplete' is offered only for the free-text types, which are
	// the ones rendered as a plain input/textarea and therefore the only ones
	// the browser's autofill heuristics can act on. Every other type either
	// renders a widget antd already opts out of (select, picker) or holds a
	// shape no autofill token describes.
	// 'scan' offers a camera button on the input, as the rightmost member of
	// its Space.Compact affix group (docs/platform-standards.md). Offered for
	// the three types a decoded STRING can fill: a short text, a URL, and a
	// lookup (where the string is matched against a record rather than
	// stored). Unlike 'quickFilter', the API REFUSES it on any other type
	// rather than ignoring it, so the policy here and the API's gate have to
	// agree — and it must stay in lock-step with keepr-api
	// utils/multiAddForms/validate.js INPUT_OPTION_POLICY.
	// The data types second pass, PR 2 ("text rules", docs/plans/
	// data-types-second-pass.md §3.4): a short text's own 'maxLength', its
	// 'case' and 'trim' (write-time normalisations) and its 'pattern' (a
	// preset or a custom regex) — the TextOptions sub-editor, with 'trim' a
	// toggle row. Every one is enforced on every API write path
	// (keepr-api utils/typedElements.js) and refused on any other type there.
	// 'unique' (docs/plans/data-types-second-pass.md §3.3, PR 3): two items of
	// the card can't share a value — offered on the types whose value is one
	// thing a person or an import types: short text, the numbers, email,
	// phone and url. The API refuses it on every other type, beside
	// `sequence` (unique already), `drivenFrom` and `allowMultiple`, and
	// inside an element set (keepr-api utils/uniqueValues.js).
	// 'defaultValue' (PR 4, docs/plans/data-types-second-pass.md §3.6): the
	// value a new item starts with, on every create path (keepr-api
	// utils/elementDefaults.js), set in the element editor's DefaultValueField
	// — which renders the element's own input. Offered on every type that
	// holds a typed literal: the plain texts, choice, boolean, rating, the
	// numbers, the date family, the contact types and currency. The API
	// refuses a default beside `drivenFrom`, `unique`, `sequence` or the
	// account link.
	// 'mask' (KPR-1348, utils/maskedElements.js): "Mask value" — the value is
	// hidden on screens until someone shows or copies it. A short text, a long
	// text and a number only; the API refuses it on every other type, strictly
	// (no type change carries it), so pruneOptionsForType drops it on a retype.
	'text-small': [...COMMON_OPTIONS, 'disableAutocorrect', 'enableAutocomplete', 'scan', 'maxLength', 'case', 'trim', 'pattern', 'unique', 'defaultValue', 'mask'],
	// 'extendedLength' ("Allow longer text") lifts the two long types to their
	// extended ceiling — 50,000 / 250,000 characters instead of 20,000 /
	// 100,000 (utils/elementFormat/text.js, vendored from keepr-api; the
	// owner's ruling D6 in docs/plans/data-types-second-pass.md). A short text
	// has none: one that needs more is a long text. The API refuses the key
	// anywhere else (utils/elementOptionKeys.js).
	// A long text may carry its own 'maxLength' too (PR 2), under the
	// ceiling "Allow longer text" sets.
	'text-large': [...COMMON_OPTIONS, 'enableAutocomplete', 'extendedLength', 'maxLength', 'defaultValue', 'mask'],
	'rich-text': [...COMMON_OPTIONS, 'extendedLength'],
	// 'quickFilter' promotes an element to a one-click chip on the list's
	// filter row (docs/plans/card-editor-dates-groups-filters.md Part 5,
	// Block B). Offered for the three types whose values are a short, closed
	// set somebody would pick from a menu: a choice's choices, a yes/no's two
	// words, a lookup's items. A date has periods, a number has ranges and a
	// paragraph has neither — none of them is a chip.
	// 'allowMultiple' ("Allow multiple", docs/plans/data-types-second-pass.md
	// §3.5, rulings D1 and D2): the value is a LIST of choice values, stored in
	// the choices' order. On `choice` only; the API refuses the key elsewhere
	// (utils/elementOptionKeys.js).
	choice: [...COMMON_OPTIONS, 'choices', 'preferChoiceIcons', 'allowMultiple', 'quickFilter', 'defaultValue'],
	// 'convertTo' is the conversion instruction a SAVED measurement element
	// carries when it is switched to a numeric type ("convert stored values
	// to [unit]"); the form shows it only for that transition
	// (utils/conversions.js planConversion) — the policy merely permits it.
	// The number-specific keys (decimals, leadingZeros, nonNegative, min, max)
	// are edited together by the NumberOptions sub-editor
	// (components/cards/NumberOptions.js) and enforced on every API write
	// path — docs/plans/data-types.md decision 3.
	// 'sequence' and 'prefix' are the server-numbering pair (docs/SCHEMA.md
	// "Sequence numbers"), edited together by SequenceOptions.js. 'prefix' is
	// offered on its own too: PO-1234 typed by hand is the same rendering
	// problem as an allocated invoice number, and the platform has one way to
	// spell it. Neither joins OPTION_KEYS_KEPT_ACROSS_TYPES: a sequence that
	// survived a change to text would leave the raw number rendering bare on
	// an element the server no longer numbers.
	// PR 7's number polish (docs/plans/data-types-second-pass.md §3.8, ruling
	// D9): 'percent' and 'thousands' are how the value reads everywhere (the
	// vendored utils/elementFormat/number.js), 'step' is what the − / +
	// buttons, antd's arrows and the slider move by, and 'control' ('slider')
	// swaps the input for a slider between a literal min and max. All four are
	// edited in NumberOptions (the two switches ride the toggle run); an
	// identifier — padding, a prefix, numbering — withholds the first two
	// (isIdentifierNumber), and the API refuses the pairing.
	// KPR-1025: 'fractions' ("Show as a fraction") on number and decimal, never
	// integer (nothing after the point to show); a toggle-run switch withheld
	// beside an identifier or a percent (fractionsAvailable).
	// 'rank' (KPR-1396): a manual order — number and decimal only (keepr-api
	// utils/elementOptionKeys.js): a value between two others needs decimals.
	number: [...COMMON_OPTIONS, 'incrementButtons', 'defaultValue', 'convertTo', 'decimals', 'leadingZeros', 'prefix', 'sequence', 'nonNegative', 'min', 'max', 'unique', 'percent', 'thousands', 'step', 'control', 'fractions', 'mask', 'rank'],
	// Legacy numeric types keep parity with 'number'. 'integer' behaves as
	// decimals: 0, so the editor pins its decimals field rather than hiding it.
	decimal: [...COMMON_OPTIONS, 'incrementButtons', 'defaultValue', 'convertTo', 'decimals', 'leadingZeros', 'prefix', 'sequence', 'nonNegative', 'min', 'max', 'unique', 'percent', 'thousands', 'step', 'control', 'fractions', 'rank'],
	integer: [...COMMON_OPTIONS, 'incrementButtons', 'defaultValue', 'convertTo', 'decimals', 'leadingZeros', 'prefix', 'sequence', 'nonNegative', 'min', 'max', 'unique', 'percent', 'thousands', 'step', 'control'],
	// A yes/no always HAS a value (new items start at false, or at its
	// default), so 'required' would be a box nobody could fail — it is the one
	// type COMMON_OPTIONS does not apply to in full. The two labels ride the
	// switch on the form and stand in for the value everywhere it is displayed.
	boolean: ['drivenFrom', 'trueLabel', 'falseLabel', 'quickFilter', 'defaultValue'],
	// The date-specific keys (min, max, rangeEnd, minuteStep) are edited
	// together by the DateOptions sub-editor (components/cards/DateOptions.js)
	// and enforced on every API write path — docs/plans/
	// card-editor-dates-groups-filters.md Part 2c. `minuteStep` is withheld
	// from `date` (there is no clock to step) and `rangeEnd` from `time` (a
	// span of two wall clocks with no day is not a duration anyone can read).
	//
	// The second pass adds two more, both the Dates panel's (utils/dateOptions):
	// `weekdays` and `precision` on `date` alone — a date & time already names
	// an hour, so a weekday rule would be half a rule, and "September 2026 at
	// 3pm" is not a reading anyone wants. It added `allowMultiple` too, which
	// left again: a date holds one value (the owner's ruling, 2026-10-22;
	// KPR-1146). One a saved date still carries rides along as stored
	// (pruneOptionsForType) and the Dates panel offers it only to turn off.
	date: [...COMMON_OPTIONS, 'defaultToToday', 'incrementButtons', 'showRelativeTime', 'min', 'max', 'rangeEnd', 'weekdays', 'precision', 'defaultValue'],
	'date-time': [...COMMON_OPTIONS, 'defaultToNow', 'incrementButtons', 'showRelativeTime', 'min', 'max', 'rangeEnd', 'minuteStep', 'defaultValue'],
	time: [...COMMON_OPTIONS, 'defaultToNow', 'min', 'max', 'minuteStep', 'defaultValue'],
	url: [...COMMON_OPTIONS, 'enableAutocomplete', 'scan', 'unique', 'defaultValue'],
	// The three text-adjacent types (docs/plans/data-types.md decision 1).
	// A phone's `country` is the region a national number is parsed against
	// and read back in; a location's `accept` narrows which halves (an
	// address, coordinates, or both) it will take. Email needs nothing beyond
	// the common options: an address is an address everywhere.
	//
	// 'enableAutocomplete' is deliberately absent from all three: each input
	// names its own autofill token (tel / email / street-address), because
	// these are the types whose whole purpose is to hold a way of reaching
	// someone — there is no switch to offer when the answer is always yes.
	// Mirrors the API's input policy (utils/multiAddForms/validate.js).
	phone: [...COMMON_OPTIONS, 'country', 'unique', 'defaultValue'],
	email: [...COMMON_OPTIONS, 'unique', 'defaultValue'],
	location: [...COMMON_OPTIONS, 'accept'],
	// A rating's scale ('max', 2–10 stars, default 5 — the RatingOptions
	// sub-editor, components/cards/RatingOptions.js) and half stars
	// ('allowHalf', a toggle row), both enforced on every API write path —
	// docs/plans/data-types-second-pass.md §3.1. `max` is a bare
	// number, so pruneOptionsForType's bound-shape rule already keeps it off
	// the date types; a rating turned into a number keeps it as the number's
	// upper bound.
	rating: [...COMMON_OPTIONS, 'max', 'allowHalf', 'defaultValue'],
	// The measurement-specific keys are edited together by the MeasurementOptions
	// sub-editor (components/cards/MeasurementOptions.js); 'incrementButtons' is
	// deliberately absent — a stepper has no meaning across units.
	// 'convertFrom' is the mirror of 'convertTo': a saved numeric element
	// switched to measurement says what unit its existing values are in.
	// 'ownUnits' (KPR-1109): units the author names, edited by MeasurementOptions too.
	measurement: [...COMMON_OPTIONS, 'measure', 'measures', 'ownUnits', 'defaultUnit', 'units', 'min', 'max', 'display', 'displayRules', 'defaultValue', 'convertFrom', 'fractions'],
	// Money (docs/plans/currency.md): the allowlist and its default, MAJOR-unit
	// bounds per entered currency, the no-negatives rule and the symbol/code
	// style — all edited together by the CurrencyOptions sub-editor
	// (components/cards/CurrencyOptions.js). 'convertFrom' is the conversion
	// instruction a saved numeric element carries when switched to currency
	// ({ currency }); 'incrementButtons' is absent for the measurement reason.
	// A default amount ('defaultValue', { value, currency } in major units —
	// ruling D8) is the DefaultValueField's, and a NEW currency element starts
	// with "No negative amounts" on (D8; the API writes it too).
	currency: [...COMMON_OPTIONS, 'currencies', 'defaultCurrency', 'min', 'max', 'nonNegative', 'display', 'convertFrom', 'defaultValue'],
	// A color (docs/plans/data-types-second-pass.md §4.1) has no options of
	// its own: the common ones and a default color. Never 'unique' — a color
	// is an attribute many items share, not an identifier (keepr-api
	// utils/elementOptionKeys.js says the same).
	color: [...COMMON_OPTIONS, 'defaultValue'],
	// 'defaultToMe' opens a new item with the signed-in person's own record
	// of the lookup's card (utils/lookupMe.js). The editor shows it only
	// when that card's family carries an account link — the API refuses it
	// otherwise (400 default_to_me_unavailable) — and the API refuses it on
	// any other type (400 invalid_options), so it is card-lookup's alone.
	// 'filter' / 'strict' (docs/plans/data-types-second-pass.md §3.7, PR 6):
	// the picker's stored filter over the target card's items and whether a
	// record outside it is refused — LookupFilterOptions, under the card select.
	'card-lookup': [...COMMON_OPTIONS, 'lookupCardId', 'allowMultiple', 'quickFilter', 'scan', 'defaultToMe', 'filter', 'strict'],
	// A reference to a keepr account (docs/plans/element-sets-and-me.md).
	// `allowMultiple` holds several accounts; `identity` makes the element
	// the card's ACCOUNT LINK (one item per account per collection, what
	// "me" resolves through) and is refused with `allowMultiple` — a link
	// names one person. Both are edited together by the UserOptions
	// sub-editor (components/cards/UserOptions.js). 'quickFilter' promotes a
	// "Me" chip. Mirrors the API's option policy (utils/userElements.js).
	user: [...COMMON_OPTIONS, 'allowMultiple', 'identity', 'quickFilter'],
	// A file (docs/plans/data-types-file-element.md §2, the 10b contract):
	// 'accept' ('any' | 'image' — "Photos only" means the server's own check
	// found a photo; a PDF never is one), 'allowMultiple' (a list of at most
	// 20, in the order given) and 'maxSizeMb' (a whole number 1–100; absent
	// is the platform's 100) — the FileOptions sub-editor. 'required' and
	// 'requiredWhen' apply. Never 'drivenFrom' (it leaves COMMON_OPTIONS
	// here), 'unique', 'defaultValue' or 'sequence': the API refuses each on a
	// file element at card save (400 invalid_options). 'duplicate' is the
	// card editor's own row, pre-set to 'blank' on a NEW file element (G5).
	file: ['required', 'requiredWhen', 'accept', 'allowMultiple', 'maxSizeMb'],
	// "Me too" (KPR-390): how many people said so, a count keepr keeps from
	// who confirmed. NONE of its own — the element's label is the button's
	// words — and none of the common ones: a count nobody types is never
	// required (nor conditionally), driven, defaulted, unique or numbered; the
	// API refuses each at card save (400 invalid_options; keepr-api
	// utils/confirmationValues.js REFUSED_OPTIONS). An explicit empty list, not
	// a missing entry: a missing one falls through to `default`, which offers
	// the common three. 'help' and 'group' still apply (kept across types).
	confirmations: [],
	// A repeating group (KPR-679; keepr-api utils/repeatingGroups.js, KPR-675):
	// its column list, the fewest and most entries an item holds and whether it
	// offers heading entries — RepeatingGroupOptions. An explicit list, not the
	// default: the every-type `required`, `requiredWhen` and `drivenFrom` do not
	// apply (say how many entries it needs with `minEntries`; its entries are
	// written by people), and the API refuses each at card save (400
	// invalid_options). 'help', 'group' and 'duplicate' still apply.
	'repeating-group': ['columns', 'minEntries', 'maxEntries', 'sections'],
};

// `followable` (keepr-api docs/SCHEMA.md § Card definition › Followable
// elements; KPR-391): whether a change to the element tells the people who
// follow the item. Offered on the types whose value reads on its own in a
// one-line notice — the API's FOLLOWABLE_TYPES (utils/elementOptionKeys.js),
// which refuses it on card-lookup and user (the value is another record's
// title or a person's name) and has no use for it on file, rich text or a
// location. Its own row in the card editor, not a plain toggle: ABSENT means
// "on for a single-value choice, off for everything else", so the switch
// shows what the element does and writes only a departure from that default.
export const FOLLOWABLE_TYPES = ['text-small', 'text-large', 'choice', 'number', 'decimal', 'integer', 'boolean', 'date', 'date-time', 'time', 'url', 'phone', 'email', 'rating', 'measurement', 'currency', 'color'];
FOLLOWABLE_TYPES.forEach((type) => {
	if (OPTION_POLICY[type] && !OPTION_POLICY[type].includes('followable')) OPTION_POLICY[type] = [...OPTION_POLICY[type], 'followable'];
});

/** What an element with no `followable` of its own does: a single-value choice is followed. */
export const followableByDefault = element => element?.dataType === 'choice' && element?.options?.allowMultiple !== true;

/** Is a change to this element told to the item's followers? The API's own reading (utils/itemFollows.js isFollowable). */
export const isFollowableElement = (element) => {
	if (!FOLLOWABLE_TYPES.includes(element?.dataType)) return false;
	const value = element?.options?.followable;
	if (value === true || value === false) return value;
	return followableByDefault(element);
};

export const FOLLOWABLE_TOGGLE = {
	key: 'followable',
	label: 'Tell followers when this changes',
	description: 'People who follow an item hear when this field changes, with the old and new value. On by default for a single choice, like a status.',
};

// Options for an element: its type's, and nothing a list carries. Choices live
// only on choice elements (the owner's ruling, 2026-10-19; KPE-033): a list on
// any other type is inert, kept for a change back to Choice
// (pruneOptionsForType) and neither shown nor editable while the element is
// not a Choice. (Until KPR-1015 any element that carried a list kept its
// Choices editor and Use choice icons — the legacy text-with-choices
// construct the migration KPR-1012 converted away.)
export const getOptionKeysForElement = (element) => {
	const keys = [...(OPTION_POLICY[element?.dataType] || OPTION_POLICY.default)];
	// Server numbering OWNS the value, so the two other ways of putting a
	// value there stop being offered while it is on. The API refuses both
	// pairings outright (400 invalid_options), and the honest editor answer
	// is not to show a field whose save would be refused. Removing the key
	// only hides the control — a configuration already stored is left
	// untouched, so turning numbering back off finds it intact, and
	// SequenceOptions refuses to turn numbering ON while either is set.
	// 'unique' goes with them: a numbered value is unique already, and the API
	// refuses the pairing (400 invalid_options). The card editor keeps the
	// switch visible while it is ON (CardDefinitionForm), so it can be turned off.
	// PR 7's four go with them: a numbered value is an identifier (never a
	// percentage, never grouped — the API refuses both pairings), and nobody
	// types it, so a step or a slider would move nothing. The card editor
	// keeps a stored percent / thousands switch visible while ON.
	if (element?.options?.sequence) {
		return keys.filter(key => !SEQUENCE_WITHHOLDS.includes(key));
	}
	// A MANUAL ORDER (KPR-1396) owns its value the same way: keepr writes it
	// when items are made and moved, so nothing that would refuse, round or
	// pre-fill that value is offered (the API refuses each pairing, 400
	// invalid_options), and nobody types it.
	if (element?.options?.rank === true) {
		return keys.filter(key => !RANK_WITHHOLDS.includes(key));
	}
	return keys;
};

const SEQUENCE_WITHHOLDS = ['defaultValue', 'drivenFrom', 'unique', 'percent', 'thousands', 'step', 'control', 'fractions', 'rank'];
const RANK_WITHHOLDS = ['defaultValue', 'drivenFrom', 'unique', 'required', 'requiredWhen', 'sequence', 'decimals', 'min', 'max', 'nonNegative', 'step', 'control', 'incrementButtons'];

/**
 * May "Manual order" be turned on (KPR-1396)? Not while anything it cannot
 * live beside is set — the API refuses each pairing — so the switch is
 * offered only on an element free of them (the card editor keeps it visible
 * while ON, so it can be turned off).
 */
export const rankAvailable = (options = {}) => {
	const o = options || {};
	if (Array.isArray(o.requiredWhen) && o.requiredWhen.length) return false;
	return !['sequence', 'drivenFrom', 'defaultValue', 'decimals', 'min', 'max', 'nonNegative', 'unique', 'required']
		.some(key => o[key] !== undefined && o[key] !== null && o[key] !== '' && o[key] !== false);
};

const isSetOption = value => value !== undefined && value !== null && value !== '' && value !== false;

/**
 * Is this number spelled as an IDENTIFIER — padded digits, a prefix, or
 * server numbering? An identifier is never a percentage and never grouped
 * (PR 7, docs/plans/data-types-second-pass.md §3.8): the API refuses the
 * pairing (400 invalid_options), and the shared formatter lets the
 * identifier win. The editors withhold "Show as a percent" and "Group
 * thousands" while this is true, and Leading zeros and Prefix while either
 * switch is on — whichever came first keeps the field.
 */
export const isIdentifierNumber = options => !!options
	&& (isSetOption(options.leadingZeros) || isSetOption(options.prefix) || isSetOption(options.sequence));

/** Does the number show as an amount (a percent, grouped, or a fraction)? */
export const showsAsAmount = options => !!options && (options.percent === true || options.thousands === true || options.fractions === true);

/**
 * May "Show as a fraction" be turned on (KPR-1025)? Not beside an identifier
 * (padding, a prefix, numbering), a percent, or decimals 0 or 1 (they store
 * 1/8 as 0 or 0.1, which no fraction is near) — the API refuses each pairing.
 * A measurement has none of those, so it is always available there.
 */
export const fractionsAvailable = options => !isIdentifierNumber(options)
	&& !(options && options.percent === true)
	&& !(options && Number.isInteger(options.decimals) && options.decimals < 2);

/** May "Show as a percent" be turned on? Not beside an identifier or "Show as a fraction". */
export const percentAvailable = options => !isIdentifierNumber(options) && !(options && options.fractions === true);

/**
 * Can this element be a slider? Only between a literal minimum below a
 * literal maximum — the slider's two ends (the API refuses anything else).
 */
export const sliderAvailable = options => !!options
	&& typeof options.min === 'number' && Number.isFinite(options.min)
	&& typeof options.max === 'number' && Number.isFinite(options.max)
	&& options.min < options.max;

/**
 * The decimals a step must fit for an element of `dataType` — an integer
 * keeps none whatever its options say; otherwise the element's own decimals,
 * or null (the platform's finest) when it sets none.
 */
export const stepDecimalsFor = (dataType, options) => (dataType === 'integer' ? 0 : decimalsOf(options || {}));

// The option keys a type change never drops, whatever the new type offers:
// 'required' and 'help' say the same thing about any field, 'group' is the
// item form's panel (a layout choice, not a type choice), and 'drivenFrom' is
// re-classified against the new type by the driven-from modal / the coercion
// matrix rather than thrown away — losing a configured rule because someone
// tried a type and changed their mind back would be the worse answer.
//
// 'isTitle' is here for a different reason: it is not an option of a type at
// all. It says which element NAMES the item, and no policy list offers it
// (this form uses the display template instead), but the platform reads it
// everywhere a card carries one — the KQL `title:` field, {{title}} in
// automations and quick add, the export's title column, the collection
// schema. Pruning it would silently re-title every item of the card.
//
// 'duplicate' ("Leave blank when duplicating") joins them for the reason
// 'help' is here: it says something about the FIELD — "this value is taken
// fresh every time" — that no type change makes untrue.
export const OPTION_KEYS_KEPT_ACROSS_TYPES = ['required', 'help', 'group', 'drivenFrom', 'isTitle', 'duplicate'];

// `options.duplicate` (keepr-api docs/SCHEMA.md § Card definition): what a
// Duplicate does with the element. 'blank' is the only value — the copy
// starts the field as a new item would. Honoured by the item form's plain
// Duplicate and by a copyAll duplicate profile (resolved on the API).
export const DUPLICATE_BLANK = 'blank';
export const DUPLICATE_BLANK_TOGGLE = {
	key: 'duplicate',
	label: 'Leave blank when duplicating',
	description: 'A duplicate starts this field empty (or at its default) instead of copying the original.',
};
export const isBlankOnDuplicate = element => element?.options?.duplicate === DUPLICATE_BLANK;

/**
 * pruneOptionsForType(options, dataType, previousType) -> options
 *
 * The options an element (or a multi add ad hoc input) keeps when its data
 * type changes: those the NEW type offers, plus the four above, plus a choice
 * list (below). Everything else is DROPPED rather than hidden — a stale
 * `measure` riding invisibly along in the payload is a configuration nobody
 * can see and the API would have to decide about. The choice list is the one
 * kept on purpose: inert off a Choice, waiting for a change back. A save
 * sends it only as stored (utils/choiceValues withInertChoicesAsStored), and
 * the multi add editor, whose inputs store no values, drops it.
 *
 * `previousType` (optional) is the type the element is LEAVING. It settles
 * the one class of key whose staleness the new type alone cannot see: a date
 * bound, or a range pairing, that NAMES another element. The name still
 * resolves, but the element it names kept the OLD type, so the bound is now
 * between two different date types — which the API refuses.
 *
 * Both editors share this one rule (the card definition form and the multi
 * add editor's inputs), so a type change means the same thing in both.
 * The number <-> measurement conversion instructions are unaffected: they are
 * computed at save time from the SAVED element (utils/conversions.js), and
 * 'convertTo' / 'convertFrom' are offered by the types that need them.
 */
// `min` and `max` are the one pair of keys TWO families spell differently: a
// number's is a bare number, a date's is a bound object ({ kind: 'literal' |
// 'relative' | 'element', … } — utils/dateBounds). Both families offer the
// key, so the allowlist alone would carry a date's bound into a number
// element, where the editor cannot show it, the reader cannot see it and the
// API refuses the save (400 invalid_options). A key whose SHAPE the new type
// cannot read is as stale as one it does not offer at all — and that goes for
// the shapes one DATE type cannot read either: a wall clock has no "today" to
// be relative to, only a date & time has a "now" to anchor to, and 'HH:mm' is
// not a day. `boundFitsType` is the one module that knows (utils/dateBounds).
const DATE_TYPES_FOR_BOUNDS = ['date', 'date-time', 'time'];

const boundShapeFits = (key, value, dataType) => {
	if (key !== 'min' && key !== 'max') return true;
	const isBoundObject = !!value && typeof value === 'object' && !Array.isArray(value);
	if (!DATE_TYPES_FOR_BOUNDS.includes(dataType)) return !isBoundObject;
	return isBoundObject && boundFitsType(value, dataType);
};

// A bound (or a range) that names another ELEMENT is about two types, and the
// sibling keeps its own: crossing from one date type to another leaves a link
// the API refuses ("names 'starts', which is not a date & time element").
const namesAnotherElement = (key, value) => (
	key === 'rangeEnd'
	|| ((key === 'min' || key === 'max') && !!value && typeof value === 'object' && value.kind === 'element')
);

// 'allowMultiple' is the one key several unrelated families spell the same.
// On a card lookup it means a list of item ids, and nothing downstream reads
// one family's list as another's. So it survives a type change only WITHIN
// its family. A user element's list is a list of ACCOUNTS — its own family,
// so a lookup turned into a user element (or back) starts single. A choice's
// list is a list of CHOICE VALUES (PR 5) — its own family too, so a type
// change into or out of Choice drops it. A file's list is a list of
// ATTACHMENTS — its own family again.
// A date's is no family any more: a date holds one value (KPR-1146), so a type
// change into a date or a date & time never carries one (keepsDateList below).
const MULTI_FAMILIES = [['card-lookup'], ['user'], ['choice'], ['file']];

const multiFamilyOf = dataType => MULTI_FAMILIES.find(family => family.includes(dataType)) || null;

const multiSurvivesTypeChange = (dataType, previousType) => {
	if (!previousType || previousType === dataType) return true;
	const family = multiFamilyOf(dataType);
	return !!family && family.includes(previousType);
};

// `display` is the other key two families spell differently: a
// measurement's is `{ unit, precision }`, a currency's `{ style }`. Neither
// reads the other's, and the API refuses a currency display with an unknown
// style — so it survives a type change only within its own family.
const DISPLAY_FAMILIES = [['measurement'], ['currency']];

// `accept` is a third key two families spell differently: a location's is
// 'address' | 'coordinates' (absent = both), a file's 'any' | 'image'. Neither
// reads the other's, so it survives a type change only within its family.
const ACCEPT_FAMILIES = [['location'], ['file']];

const acceptSurvivesTypeChange = (dataType, previousType) => {
	if (!previousType || previousType === dataType) return true;
	const family = ACCEPT_FAMILIES.find(f => f.includes(dataType));
	return !!family && family.includes(previousType);
};

// The kept-across-types keys a FILE element still refuses: a file is never
// driven (the API refuses `drivenFrom` on one at card save), so a type change
// into file drops the rule rather than carrying it into a save that fails.
const FILE_REFUSES = ['drivenFrom'];
// And a CONFIRMATIONS element (KPR-390) refuses three of them: a count people
// keep is never required, driven or the item's title (keepr-api
// utils/confirmationValues.js). 'help', 'group' and 'duplicate' ride along —
// the API takes them, and a copy never carries a count anyway.
const CONFIRMATIONS_REFUSES = ['required', 'drivenFrom', 'isTitle'];
// A REPEATING GROUP (KPR-679) refuses four: a list of entries is required by
// `minEntries`, never driven, and never names the item.
const GROUP_REFUSES = ['required', 'requiredWhen', 'drivenFrom', 'isTitle'];
const REFUSED_KEPT_KEYS = { file: FILE_REFUSES, confirmations: CONFIRMATIONS_REFUSES, 'repeating-group': GROUP_REFUSES };

/** The kept-across-types keys an element of `dataType` may still carry. */
export const keptAcrossTypesFor = dataType => OPTION_KEYS_KEPT_ACROSS_TYPES
	.filter(key => !(REFUSED_KEPT_KEYS[dataType] || []).includes(key));

const displaySurvivesTypeChange = (dataType, previousType) => {
	if (!previousType || previousType === dataType) return true;
	const family = DISPLAY_FAMILIES.find(f => f.includes(dataType));
	return !!family && family.includes(previousType);
};

// A text element's own `maxLength` survives a type change only where the new
// type's ceiling still holds it: a long text's 5,000 turned into a short text
// (255) is a limit the API refuses (400 invalid_options), so it is dropped
// rather than carried into a save that fails.
const maxLengthFits = (key, value, dataType, options) => (
	key !== 'maxLength' || maxLengthOf(dataType, { ...(options || {}), maxLength: value }) !== null
);

// A default's SHAPE belongs to its type (a string, a choice value, a number,
// a `{ kind, value }` date, a `{ value, currency }` amount), so it survives a
// type change only inside a family that reads it the same way: the numbers
// (`number` / `decimal` / `integer`) and the two plain texts. Carried anywhere
// else it is a value the new type cannot hold — the API refuses the save
// (400 invalid_options { option: 'defaultValue' }) — so it is dropped.
const DEFAULT_FAMILIES = [['number', 'decimal', 'integer'], ['text-small', 'text-large']];

// A step (PR 7) survives a type change only while it still fits the new
// type's decimals: a decimal's 0.5 turned into an integer is a step the API
// refuses (400 invalid_options), so it is dropped rather than carried into a
// save that fails. Within the numbers it otherwise rides along, as the
// other number options do; every other type drops it with the allowlist.
const stepFitsType = (key, value, dataType, options) => (
	key !== 'step' || stepFits(value, stepDecimalsFor(dataType, options))
);

const defaultSurvivesTypeChange = (dataType, previousType) => {
	if (!previousType || previousType === dataType) return true;
	const family = DEFAULT_FAMILIES.find(f => f.includes(dataType));
	return !!family && family.includes(previousType);
};

// A choice LIST survives every change of type (the owner's ruling, 2026-10-19;
// KPE-033): Choice -> text keeps it, inert — nothing reads it and the editor
// neither shows nor edits it — so text -> Choice reuses it, as the API does
// (KPR-1014). It is the one key kept that the new type does not offer. A
// repeating group is the exception: the API refuses `choices` on a group (a
// group holds entries, not choices), so the list is dropped into one.
const CHOICES_KEY = 'choices';
const choicesSurviveTypeChange = dataType => dataType !== 'repeating-group';

// A date holds one value (the owner's ruling, 2026-10-22; KPR-1146), so
// `allowMultiple` is no date's option. One a SAVED date still carries — a
// legacy list of dates, until its values are cut to the earliest — rides
// along only while the element stays that very type: the save echoes it as
// stored (keepr-api keeps an unchanged one and refuses an added one), and a
// trip away and back restores it. Any type change into a date drops it, which
// the save turns into B16's question (each item keeps its earliest date).
const DATE_LIST_TYPES = ['date', 'date-time'];
const keepsDateList = (key, value, dataType, previousType) => key === 'allowMultiple'
	&& value === true && DATE_LIST_TYPES.includes(dataType) && previousType === dataType;

export const pruneOptionsForType = (options, dataType, previousType = null) => {
	const allowed = new Set([
		...(OPTION_POLICY[dataType] || OPTION_POLICY.default),
		...keptAcrossTypesFor(dataType),
		...(choicesSurviveTypeChange(dataType) ? [CHOICES_KEY] : []),
	]);
	const crossedDateTypes = !!previousType
		&& previousType !== dataType
		&& DATE_TYPES_FOR_BOUNDS.includes(previousType)
		&& DATE_TYPES_FOR_BOUNDS.includes(dataType);
	const next = {};
	Object.keys(options || {}).forEach(key => {
		if (keepsDateList(key, options[key], dataType, previousType)) { next[key] = options[key]; return; }
		if (!allowed.has(key) || options[key] === undefined) return;
		// An EMPTY list (or no list at all) has nothing to keep for a change back.
		if (key === CHOICES_KEY && dataType !== 'choice' && !(Array.isArray(options[key]) && options[key].length)) return;
		if (!boundShapeFits(key, options[key], dataType)) return;
		if (crossedDateTypes && namesAnotherElement(key, options[key])) return;
		if (key === 'allowMultiple' && !multiSurvivesTypeChange(dataType, previousType)) return;
		if (key === 'display' && !displaySurvivesTypeChange(dataType, previousType)) return;
		if (key === 'accept' && !acceptSurvivesTypeChange(dataType, previousType)) return;
		if (!maxLengthFits(key, options[key], dataType, options)) return;
		if (key === 'defaultValue' && !defaultSurvivesTypeChange(dataType, previousType)) return;
		if (!stepFitsType(key, options[key], dataType, options)) return;
		next[key] = options[key];
	});
	return next;
};

export const elementSupportsOption = (element, optionKey) => (
	getOptionKeysForElement(element).includes(optionKey)
);

// The boolean options every editor renders as toggle rows, in display
// order, each with the one line that says what turning it on does. The card
// element editor and the multi add editor's ad hoc inputs share these so an
// option reads the same wherever it is set; each editor filters the list to
// the keys its policy offers for the type.
export const ELEMENT_OPTION_TOGGLES = [
	{ key: 'required', label: 'Required', description: "Items can't be saved without a value." },
	// Right after Required, its natural pair ("every item has one, and no two
	// share it"). No two items of the card may hold the same value (keepr-api
	// utils/uniqueValues.js); text and email compare ignoring case.
	{ key: 'unique', label: 'Unique values', description: "Two items of this card can't share a value." },
	{ key: 'disableAutocorrect', label: 'Disable autocorrect', description: "Turn off the keyboard's autocorrect for this field." },
	// Off by default for every element: an element named 'name' or 'address'
	// is about the ITEM, and the browser would happily offer the signed-in
	// person's own details for it. Turn this on for the rare field that
	// really does hold the reader's own information.
	{ key: 'enableAutocomplete', label: 'Allow browser autofill', description: "Let the browser offer the reader's own details here." },
	{ key: 'preferChoiceIcons', label: 'Use choice icons', description: "Show each choice's icon instead of a plain label." },
	{ key: 'defaultToToday', label: 'Default to today (new items)', description: "New items start with today's date." },
	{ key: 'defaultToNow', label: 'Default to now (new items)', description: 'New items start with the current date and time.' },
	{ key: 'incrementButtons', label: 'Increment buttons', description: 'Show − / + steppers beside the number.' },
	{ key: 'nonNegative', label: 'Disallow negatives', description: 'Refuse values below zero.' },
	// KPR-1396: a manual order. keepr writes the number; a list sorted by it
	// reorders by drag (and Move up / Move down in an item's menu).
	{ key: 'rank', label: 'Manual order', description: 'Keep items in the order people drag them into, in a list sorted by this field. keepr sets the number; nobody types it.' },
	// PR 7 (§3.8, ruling D9): the value is the number shown — 12.5 means
	// 12.5 % — so nothing is multiplied. Withheld beside an identifier.
	{ key: 'percent', label: 'Show as a percent', description: 'Show a % sign after the value — 12.5 shows as 12.5%. Not with a prefix or leading zeros.' },
	{ key: 'thousands', label: 'Group thousands', description: 'Show 1234567 as 1,234,567. Not with a prefix or leading zeros.' },
	// KPR-1025: halves, thirds, quarters and eighths, within 0.005; anything
	// else keeps its decimal. Typing a fraction works whether this is on or not.
	{
		key: 'fractions',
		label: 'Show as a fraction',
		description: 'Show 1.5 as 1 1/2 and 0.75 as 3/4: halves, thirds, quarters and eighths. Not with a percent, a prefix or leading zeros.',
		describe: dataType => (dataType === 'measurement'
			? 'Show 1.5 cups as 1 1/2 cups and 3.25 in as 3 1/4 in: halves, thirds, quarters and eighths.'
			: 'Show 1.5 as 1 1/2 and 0.75 as 3/4: halves, thirds, quarters and eighths. Not with a percent, a prefix or leading zeros.'),
	},
	{ key: 'showRelativeTime', label: 'Show time ago / from now', description: 'Add "3 days ago" or "in 2 weeks" beside the date.' },
	{
		key: 'allowMultiple',
		label: 'Allow multiple',
		description: 'Accept more than one selection.',
		// A file element's list is files, at most 20, kept in the order given
		// (the first is the one a tile shows) — the 10b contract.
		describe: dataType => (dataType === 'file' ? 'Hold up to 20 files, in the order they were added.' : 'Accept more than one selection.'),
	},
	{ key: 'quickFilter', label: 'Offer as a quick filter', description: 'Add a one-click chip for this field above the list.' },
	{ key: 'scan', label: 'Scan with the camera', description: 'Add a camera button that fills this field from a barcode or QR code.' },
	{ key: 'defaultToMe', label: "Fill with the signed-in person's record", description: "New items start with the record linked to whoever is signed in. If they have none, the field starts blank." },
	// A rating's half stars (the scale itself is RatingOptions' Select).
	{ key: 'allowHalf', label: 'Allow half stars', description: 'A rating can be 3.5 as well as 3 or 4.' },
	// The two long types' extended ceiling. `describe` says the numbers for
	// the element's own type; the editors use it over `description`.
	{
		key: 'extendedLength',
		label: 'Allow longer text',
		description: 'Hold more characters than this type normally allows.',
		describe: dataType => `Up to ${formatLimit(ceilingFor(dataType, { extendedLength: true }))} characters instead of ${formatLimit(ceilingFor(dataType))}.`,
	},
	// A short text's write-time trim (PR 2). The guide used to promise keepr
	// never trims a value; it still does not unless this is on.
	{ key: 'trim', label: 'Trim spaces', description: 'Remove spaces before and after the value when it is saved.' },
	// KPR-1348: a display setting, never called secure — the value is stored,
	// exported and read by assistants as it is. Free text never finds it.
	{ key: 'mask', label: 'Mask value', description: "Show dots instead of the value until someone chooses Show or Copy. Search won't find it; exports and assistants still read it." },
];

/**
 * The line an element editor shows beside a text-shaped type: "Up to 255
 * characters." — the ceiling the element is held to on every write path
 * (utils/elementFormat/text.js), the extended one when `extendedLength` is
 * on. null for a type with no ceiling.
 */
export const lengthLimitText = (dataType, options) => {
	const limit = ceilingFor(dataType, options || {});
	if (limit === null) return null;
	// An author's own maxLength (PR 2) is the number that binds; the type's
	// ceiling is said beside it, so neither is a surprise.
	const own = maxLengthOf(dataType, options || {});
	return own !== null && own < limit
		? `Up to ${formatLimit(own)} characters (this element's limit; the type holds ${formatLimit(limit)}).`
		: `Up to ${formatLimit(limit)} characters.`;
};

/** A toggle row's one line for this element's type (its `describe`, when it has one). */
export const toggleDescription = (row, dataType) => (typeof row.describe === 'function' ? row.describe(dataType) : row.description);

// A multi add form's ad hoc inputs take element-grade options, minus the
// ones that only make sense on a stored element of a card: a driven value
// (nothing stores the input), the stored-value conversion instructions, the
// measurement display rules (the input is only ever entered, never shown
// back) and the item form's panel grouping. The API drops these on the floor,
// so the editor never offers them. ('isTitle' needs no entry: an input is
// never given one, and the API's input whitelist drops it anyway.)
// ('quickFilter' joins them for the same reason: an input belongs to a form,
// and there is no list of inputs to put a chip above. 'identity' too: an
// account link is a property of a card's items, and an input stores none.)
// `requiredWhen` joins them for the same reason `drivenFrom` is there: an ad
// hoc input's condition would have to name an element, and an input is not on
// a card — the API's input whitelist does not carry it either.
// `sequence` joins them for the sharpest version of the same reason: a counter
// is keyed on the card that DECLARES the element, and an ad hoc input is
// declared by no card at all — a numbered input could only ever be a promise
// nothing keeps. `prefix` follows it here rather than on its own merits: it is
// half of how a number is spelled, the API's input whitelist does not carry
// it, and offering it alone would be a field the save silently drops.
// `defaultToMe` joins them because the API's input whitelist does not carry
// it: an input's lookup still gets the "Me" button (the shared input), but a
// multi add form opens with what its mappings say, nothing else.
// `unique` joins them for the sequence reason: its values are held per card
// that DECLARES the element, and an ad hoc input is declared by no card — the
// API's input whitelist does not carry it.
// A lookup's `filter` / `strict` join them: the API's input whitelist does not
// carry them, and the step element an input fills judges its own strict
// filter on submit (keepr-api utils/lookupFilter.js).
// 'followable' (KPR-391) is a CARD element's: it says what an item's followers
// hear, and an ad hoc input belongs to a form, not to an item's card.
// 'measures' (KPR-1085): an ad hoc measurement input takes one measure — the
// API's input whitelist (keepr-api utils/multiAddForms/validate.js) does not
// carry several. 'ownUnits' (KPR-1109): nor units of its own.
// 'mask' (KPR-1348) is a card element's display setting: an ad hoc input
// stores nothing to mask, and the API's input whitelist does not carry it.
export const INPUT_OPTION_KEYS_EXCLUDED = ['drivenFrom', 'requiredWhen', 'convertTo', 'convertFrom', 'display', 'displayRules', 'group', 'quickFilter', 'identity', 'sequence', 'rank', 'prefix', 'defaultToMe', 'unique', 'filter', 'strict', 'followable', 'measures', 'ownUnits', 'mask'];

// The date keys a multi add form's ad hoc input does not take. A bound that
// names another ELEMENT has nothing to name (an input belongs to the form,
// not to a card), a range is a pair of card elements by definition, and the
// multi add editor has no sub-panel to set a literal or relative bound in —
// so the API's input whitelist does not carry them either. (Withheld as a
// set: a `min` a number's editor can write must not become a shape a date's
// input could not.)
// (The second pass's three join them for the same reason: each is edited in
// the Dates sub-panel, which an ad hoc input has no row for, and the API's
// input whitelist does not carry them.)
const INPUT_DATE_KEYS_EXCLUDED = ['min', 'max', 'rangeEnd', 'minuteStep', 'allowMultiple', 'weekdays', 'precision'];

// The types an ad hoc input may carry a `defaultValue` on: a bare number for
// the numeric types, and — PR 4 (docs/plans/data-types-second-pass.md §3.6) —
// a choice value (a list of them on a choice input that allows multiple, PR
// 5), a yes/no and a text. The date inputs keep their Default to
// today / now; a measurement's { value, unit } and the other types' defaults
// are not on the input contract. Mirrors keepr-api
// utils/multiAddForms/validate.js INPUT_OPTION_POLICY.
export const INPUT_DEFAULT_TYPES = ['number', 'decimal', 'integer', 'choice', 'boolean', 'text-small', 'text-large'];

// The option keys an ad hoc input of `dataType` may carry.
export const getOptionKeysForInput = (dataType) => (
	(OPTION_POLICY[dataType] || OPTION_POLICY.default)
		.filter(key => !INPUT_OPTION_KEYS_EXCLUDED.includes(key))
		.filter(key => !(key === 'defaultValue' && !INPUT_DEFAULT_TYPES.includes(dataType)))
		.filter(key => !(DATE_TYPES_FOR_BOUNDS.includes(dataType) && INPUT_DATE_KEYS_EXCLUDED.includes(key)))
);
